import { Paths, PathValue } from "@mctypes/global.types";
import { useEffect } from "react";
import { Button, Paper, Stack, Title } from "@mantine/core";
import { HSpace, McInputNew, McTextAreaNew } from "../../../../../../../components";
import McDropdownNew from "../../../../../../../components/McDropdown/MCDropdownNew";
import { LuftmengdeGjennomLukeaapningEnum } from "@mcapps/shared/gkReportConstants";
import {
  IGKMaalingAvLuftmengdeGjennomInntaksfilterResultater,
  IGKRapport,
} from "@mcapps/shared/reportSchemas/gkReportSchema";
import ReportDocxImageContainer from "../../../../ReportBoard/ReportDocxImageContainer";
import {
  beregnetArealForHvertPunkt,
  beregnetAvkastLuftmengde,
  beregnetGjennomsnittligHastighet,
  beregnetLuftmengdeGjennomLukeaapning,
  beregnetTverrsnittareal,
} from "../../gkGlobalUtils";
import { beregnetLuftmengdePerVolum } from "./MaalingAvLuftmengdeGjennomInntaksfilterUtils";
const MaalingAvLuftmengdeGjennomInntaksfilter: React.FC<{
  onUpdateEditData: <P extends Paths<IGKRapport, 4>>(path: P, value: PathValue<IGKRapport, P>) => void;
  data: IGKMaalingAvLuftmengdeGjennomInntaksfilterResultater;
}> = ({ data, onUpdateEditData }) => {
  const TESTMETODER: LuftmengdeGjennomLukeaapningEnum[] = [
    LuftmengdeGjennomLukeaapningEnum.AVKASTKANAL,
    LuftmengdeGjennomLukeaapningEnum.INNTAKSFILTER,
  ];
  useEffect(() => {
    maaleResultaterArray();
  }, [data.inndata.valgtMetode]);
  useEffect(() => {
    utdataBeregninger();
  }, [
    data.inndata.maaleresultater,
    data.inndata.avkastkanalResultater.diameter,
    data.inndata.inntaksfilterResultater.arealetTilFilterflaten,
    data.inndata.maaleresultater.length,
  ]);
  const maaleResultaterArray = () => {
    let arrayLength;
    if (data.inndata.valgtMetode === LuftmengdeGjennomLukeaapningEnum.INNTAKSFILTER) {
      arrayLength = 5;
    }
    const newMaaleresultaterArray = Array.from({ length: arrayLength }, () => null);
    onUpdateEditData(
      "testResultater.maalingAvLuftmengdeGjennomInntaksfilter.inndata.maaleresultater",
      newMaaleresultaterArray as number[],
    );
  };
  const addNewRow = () => {
    const maaleresultaterArray = data.inndata.maaleresultater;
    maaleresultaterArray.push(null);
    onUpdateEditData(
      "testResultater.maalingAvLuftmengdeGjennomInntaksfilter.inndata.maaleresultater",
      maaleresultaterArray,
    );
    return maaleresultaterArray;
  };
  const removeLastRow = () => {
    const updatedMaaleresultater = data.inndata.maaleresultater;
    if (updatedMaaleresultater.length > 0) {
      updatedMaaleresultater.pop(); // Remove the last element
      onUpdateEditData(
        "testResultater.maalingAvLuftmengdeGjennomInntaksfilter.inndata.maaleresultater",
        updatedMaaleresultater,
      );
    }
  };
  const updateMaaleresultater = (resultat, i) => {
    const maaleresultater = [...data.inndata.maaleresultater];
    maaleresultater[i] = resultat;
    onUpdateEditData("testResultater.maalingAvLuftmengdeGjennomInntaksfilter.inndata.maaleresultater", maaleresultater);
  };
  const utdataBeregninger = () => {
    let tverrsnittareal: number = null;
    let arealForHvertPunkt: number = null;
    let avkastLuftmengde: number = null;
    let gjennomsnittligHastighet: number = null;
    let luftmengdeGjennomFilter: number = null;
    let luftmengdePerVolum: number = null;
    if (data.inndata.valgtMetode === LuftmengdeGjennomLukeaapningEnum.AVKASTKANAL) {
      tverrsnittareal = beregnetTverrsnittareal(data.inndata.avkastkanalResultater.diameter);
      arealForHvertPunkt = beregnetArealForHvertPunkt(tverrsnittareal, data.inndata.maaleresultater.length);
      avkastLuftmengde = beregnetAvkastLuftmengde(
        data.inndata.maaleresultater.map((resultat) => resultat),
        arealForHvertPunkt,
      );
      luftmengdePerVolum = beregnetLuftmengdePerVolum(avkastLuftmengde, data.inndata.kabinettvolum);
    }
    if (data.inndata.valgtMetode === LuftmengdeGjennomLukeaapningEnum.INNTAKSFILTER) {
      gjennomsnittligHastighet = beregnetGjennomsnittligHastighet(
        data.inndata.maaleresultater.map((resultat) => resultat),
        data.inndata.maaleresultater.length,
      );
      luftmengdeGjennomFilter = beregnetLuftmengdeGjennomLukeaapning(
        gjennomsnittligHastighet,
        data.inndata.inntaksfilterResultater.arealetTilFilterflaten,
      );
      luftmengdePerVolum = beregnetLuftmengdePerVolum(luftmengdeGjennomFilter, data.inndata.kabinettvolum);
    }
    const utdata = {
      luftmengdePerVolum: luftmengdePerVolum,
      avkastkanalBeregninger: {
        tverrsnittareal: tverrsnittareal,
        arealForHvertPunkt: arealForHvertPunkt,
        avkastLuftmengde: avkastLuftmengde,
      },
      inntaksfilterBeregninger: {
        gjennomsnittligHastighet: gjennomsnittligHastighet,
        luftmengdeGjennomFilter: luftmengdeGjennomFilter,
      },
    };
    onUpdateEditData("testResultater.maalingAvLuftmengdeGjennomInntaksfilter.utdata", utdata);
  };
  return (
    <Paper withBorder p="sm" radius="sm" bg="#fdfdff">
      <Stack gap="xs">
        <Title order={4} fw={400}>Luftmengdemåling gjennom inntaksfilter</Title>
        <div className="d-flex flex-column mt-4 mb-4">
          <McInputNew
            label={<>
              Fyll inn kabinettvolum &#40;m<sup>3</sup>&#41;:
            </>}
            value={data.inndata.kabinettvolum}
            onBlur={(e) =>
              onUpdateEditData(
                "testResultater.maalingAvLuftmengdeGjennomInntaksfilter.inndata.kabinettvolum",
                Number(e.target.value),
              )
            }
            normalizeNumbers={true}
            type="text"
            className="w-25"
          />
        </div>
        <b>Hvilken metode skal brukes?</b>
        <div className="w-25">
          <McDropdownNew
            currentValue={data.inndata.valgtMetode}
            onChange={(_, item) =>
              onUpdateEditData(
                "testResultater.maalingAvLuftmengdeGjennomInntaksfilter.inndata.valgtMetode",
                item as string,
              )
            }
            defaultTitle="Hvilken testmetode skal brukes?"
            options={TESTMETODER}
          />
        </div>
        {data.inndata.valgtMetode === LuftmengdeGjennomLukeaapningEnum.AVKASTKANAL &&
          data.inndata.kabinettvolum != null && (
            <>
              <div className="d-flex flex-column mt-4">
                <McInputNew
                  label="Fyll inn diameter på avkastskanal (m):"
                  value={data.inndata.avkastkanalResultater.diameter}
                  onBlur={(e) =>
                    onUpdateEditData(
                      "testResultater.maalingAvLuftmengdeGjennomInntaksfilter.inndata.avkastkanalResultater.diameter",
                      Number(e.target.value),
                    )
                  }
                  normalizeNumbers={true}
                  type="text"
                  className="w-25"
                />
              </div>
              <div className="d-flex justify-content-between">
                <p className="mb-0">
                  Tverrsnittsareal av kanal &#40;m<sup>2</sup>&#41; ={" "}
                  <b>{beregnetTverrsnittareal(data.inndata.avkastkanalResultater.diameter)?.toFixed(3) ?? ""}</b>
                </p>
                <p className="mb-0">
                  Formel: &#40;Ak = PI * &#40;D / 2&#41;<sup>2</sup>&#41;
                </p>
              </div>
              <hr className="mt-0" />
              <div className="mt-4">
                <b>Legg til flere punkter ved behov:</b>
                <div className="d-flex">
                  <Button color="green" onClick={addNewRow}>
                    Legg til rad
                  </Button>
                  <HSpace />
                  <Button variant="danger-filled" onClick={removeLastRow}>
                    Fjern siste rad
                  </Button>
                </div>
              </div>
              <div className="mt-4 d-flex flex-column">
                <b>Fyll inn måleresultater &#40;m/s&#41;:</b>
                {data.inndata.maaleresultater.map((a, i) => (
                  <McInputNew
                    className="mb-1 w-25"
                    key={i}
                    value={a}
                    normalizeNumbers={true}
                    type="text"
                    onBlur={(e) => updateMaaleresultater(Number(e.target.value), i)}
                  />
                ))}
              </div>
              <div className="d-flex justify-content-between">
                <p className="mb-0">
                  Areal for hvert målepunkt &#40;m<sup>2</sup>&#41; ={" "}
                  <b>
                    {beregnetArealForHvertPunkt(
                      data.utdata.avkastkanalBeregninger.tverrsnittareal,
                      data.inndata.maaleresultater.length,
                    )?.toFixed(3) ?? ""}
                  </b>
                </p>
                <p className="mb-0">Formel: &#40;Ac = Ak / N&#41;</p>
              </div>
              <hr className="mt-0" />
              <div className="d-flex justify-content-between">
                <p className="mb-0">
                  Avkastsluftmengde &#40;m<sup>3</sup>/s&#41; ={" "}
                  <b>
                    {beregnetAvkastLuftmengde(
                      data.inndata.maaleresultater.map((resultat) => resultat),
                      data.utdata.avkastkanalBeregninger.arealForHvertPunkt,
                    )?.toFixed(3) ?? ""}
                  </b>
                </p>
                <p className="mb-0">Formel: &#40;Qa = SUM&#40;Vn * Ac&#41;&#41;</p>
              </div>
              <hr className="mt-0" />
              <div className="d-flex justify-content-between">
                <p className="mb-0">
                  Luftmengde per volum &#40;&#40;m<sup>3</sup>/s&#41;/m<sup>3</sup>&#41; ={" "}
                  <b>
                    {beregnetLuftmengdePerVolum(
                      data.utdata.avkastkanalBeregninger.avkastLuftmengde,
                      data.inndata.kabinettvolum,
                    )?.toFixed(3) ?? ""}
                  </b>
                </p>
                <p className="mb-0">Formel: &#40;Va = Qa / Aluke&#41;</p>
              </div>
              <hr className="mt-0" />
            </>
          )}
        {data.inndata.valgtMetode === LuftmengdeGjennomLukeaapningEnum.INNTAKSFILTER &&
          data.inndata.kabinettvolum != null && (
            <>
              <div className="d-flex flex-column mt-4 mb-4">
                <McInputNew
                  label={<>
                    Fyll inn arealet til filterflaten &#40;m<sup>2</sup>&#41;:
                  </>}
                  value={data.inndata.inntaksfilterResultater.arealetTilFilterflaten}
                  onBlur={(e) =>
                    onUpdateEditData(
                      "testResultater.maalingAvLuftmengdeGjennomInntaksfilter.inndata.inntaksfilterResultater.arealetTilFilterflaten",
                      Number(e.target.value),
                    )
                  }
                  normalizeNumbers={true}
                  type="text"
                  className="w-25"
                />
              </div>
              <div className="d-flex flex-column mt-2">
                <b>Måletiden er 60 sekunder &#40;s&#41;:</b>
              </div>
              <div className="mt-2">
                <b>Legg til flere punkter ved behov:</b>
                <div className="d-flex">
                  <Button color="green" onClick={addNewRow}>
                    Legg til rad
                  </Button>
                  <HSpace />
                  <Button
                    variant="danger-filled"
                    onClick={removeLastRow}
                    disabled={data.inndata.maaleresultater.length === 5}
                  >
                    Fjern siste rad
                  </Button>
                </div>
              </div>
              <div className="mt-4 d-flex flex-column">
                <b>Fyll inn måleresultater &#40;m/s&#41;:</b>
                {data.inndata.maaleresultater.map((a, i) => (
                  <McInputNew
                    className="mb-1 w-25"
                    key={i}
                    value={a}
                    normalizeNumbers={true}
                    type="text"
                    onBlur={(e) => updateMaaleresultater(Number(e.target.value), i)}
                  />
                ))}
              </div>
              <div className="d-flex justify-content-between">
                <p className="mb-0">
                  Gjennomsnittlig hastighet &#40;m/s&#41; ={" "}
                  <b>
                    {beregnetGjennomsnittligHastighet(
                      data.inndata.maaleresultater.map((resultat) => resultat),
                      data.inndata.maaleresultater.length,
                    )?.toFixed(3) ?? ""}
                  </b>
                </p>
                <p className="mb-0">Formel: &#40;Va = &#40;SUM Vn&#41; / N&#41;</p>
              </div>
              <hr className="mt-0" />
              <div className="d-flex justify-content-between">
                <p className="mb-0">
                  Luftmengde gjennom inntaksfilter&#40;m<sup>3</sup>/s&#41; ={" "}
                  <b>
                    {beregnetLuftmengdeGjennomLukeaapning(
                      data.utdata.inntaksfilterBeregninger.gjennomsnittligHastighet,
                      data.inndata.inntaksfilterResultater.arealetTilFilterflaten,
                    )?.toFixed(3) ?? ""}
                  </b>
                </p>
                <p className="mb-0">Formel: &#40;Q = Va * Aluke&#41;</p>
              </div>
              <hr className="mt-0" />
              <div className="d-flex justify-content-between">
                <p className="mb-0">
                  Luftmengde per volum &#40;&#40;m<sup>3</sup>/s&#41;/m<sup>3</sup>&#41; ={" "}
                  <b>
                    {beregnetLuftmengdePerVolum(
                      data.utdata.inntaksfilterBeregninger.luftmengdeGjennomFilter,
                      data.inndata.kabinettvolum,
                    )?.toFixed(3) ?? ""}
                  </b>
                </p>
                <p className="mb-0">Formel: &#40;Va = Qa / Aluke&#41;</p>
              </div>
              <hr className="mt-0" />
            </>
          )}
        <div className="mt-4">
          <ReportDocxImageContainer
            docxImageTag="maalingAvLuftmengdeGjennomInntaksfilter"
            title="Målepunkter i rommet"
          />
          <div className="w-50">
            <McTextAreaNew
              label="Kommentar:"
              value={data.inndata.kommentar}
              onChange={(e) =>
                onUpdateEditData(
                  "testResultater.maalingAvLuftmengdeGjennomInntaksfilter.inndata.kommentar",
                  e.target.value,
                )
              }
            />
          </div>
        </div>
      </Stack>
    </Paper>
  );
};
export default MaalingAvLuftmengdeGjennomInntaksfilter;